import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { Button } from '@monolith/components/0-atoms/Button';
import Icon from '@monolith/components/0-atoms/Icon';
import { StaffImportWarningModal } from './StaffImportWarningModal';
import {
    STAFF_IMPORT_PAGE_COPY,
    validateCsvFile,
} from '../constants/staffImport';
import { staffImportTokens } from '../styles/staffImportTokens';
import { staffImportInProgressButtonStyles } from '../styles/staffImportFieldStyles';

export interface StaffImportCsvUploadFieldProps {
    id: string;
    selectedFile: File | null;
    uploadedFileName: string | null;
    uploading: boolean;
    disabled?: boolean;
    errorMessage?: string;
    hasExistingImport: boolean;
    onFileSelect: (file: File) => void;
    onUpload: () => void;
}

const FieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${staffImportTokens.spacing.fieldGap}px;
    width: 100%;
`;

const Label = styled.label`
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.label.fontSize};
    font-weight: ${staffImportTokens.typography.label.fontWeight};
    line-height: ${staffImportTokens.typography.label.lineHeight};
    color: ${staffImportTokens.colors.textDefault};
`;

const IconSlot = styled.div<{ $hasError?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
    color: #616161;
    background: ${staffImportTokens.colors.iconSlotBg};
    border: 1px solid
        ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.iconSlotBorder};
    border-right: none;
    border-radius: 12px 0 0 12px;
`;

const FileNameDisplay = styled.div<{
    $hasError?: boolean;
    $isPlaceholder?: boolean;
}>`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 12px;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.label.fontSize};
    font-weight: ${staffImportTokens.typography.label.fontWeight};
    color: ${(props) =>
        props.$isPlaceholder
            ? staffImportTokens.colors.placeholder
            : staffImportTokens.colors.textPrimary};
    background: #ffffff;
    border: 1px solid
        ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.grey3};
    border-right: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const InputGroup = styled.div<{ $hasError?: boolean; $clickable?: boolean }>`
    display: flex;
    align-items: stretch;
    width: 100%;
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

    &:focus-within ${IconSlot} {
        border-color: ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.primary};
    }

    &:focus-within ${FileNameDisplay} {
        border-color: ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.primary};
    }
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const UploadButton = styled(Button)<{
    $hasError?: boolean;
    $inProgress?: boolean;
}>`
    height: 40px;
    min-width: auto;
    gap: 8px;
    border-radius: 0 8px 8px 0;
    border: 1px solid
        ${(props) =>
            props.$hasError
                ? staffImportTokens.colors.red9
                : staffImportTokens.colors.primary};
    border-left: none;
    white-space: nowrap;

    ${({ $inProgress }) =>
        $inProgress ? staffImportInProgressButtonStyles : ''}
`;

const ErrorMessage = styled.p`
    margin: 0;
    font-family: 'Montserrat', sans-serif;
    font-size: ${staffImportTokens.typography.bodyDefault.fontSize};
    line-height: 17px;
    color: ${staffImportTokens.colors.red9};
`;

export const StaffImportCsvUploadField: React.FC<
    StaffImportCsvUploadFieldProps
> = ({
    id,
    selectedFile,
    uploadedFileName,
    uploading,
    disabled = false,
    errorMessage,
    hasExistingImport,
    onFileSelect,
    onUpload,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [localError, setLocalError] = useState<string | null>(null);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const displayName =
        selectedFile?.name ||
        uploadedFileName ||
        STAFF_IMPORT_PAGE_COPY.csvUploadPlaceholder;
    const isPlaceholder = !selectedFile && !uploadedFileName;
    const canUpload = Boolean(selectedFile) && !uploading && !disabled;
    const uploadComplete =
        Boolean(uploadedFileName) && selectedFile?.name === uploadedFileName;
    const resolvedError = errorMessage || localError || undefined;

    const applyFileSelection = (file: File) => {
        const validation = validateCsvFile(file);
        if (!validation.valid) {
            setLocalError(validation.error);
            return;
        }
        setLocalError(null);
        onFileSelect(file);
    };

    const handleFileInputChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }

        if (hasExistingImport) {
            setPendingFile(file);
            setShowReplaceModal(true);
            return;
        }

        applyFileSelection(file);
    };

    const openFilePicker = () => {
        if (disabled || uploading) {
            return;
        }
        fileInputRef.current?.click();
    };

    const handleUploadClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!selectedFile) {
            openFilePicker();
            return;
        }
        onUpload();
    };

    const handleReplaceCancel = () => {
        setShowReplaceModal(false);
        setPendingFile(null);
    };

    const handleReplaceConfirm = () => {
        if (pendingFile) {
            applyFileSelection(pendingFile);
            setPendingFile(null);
        }
        setShowReplaceModal(false);
    };

    return (
        <FieldContainer>
            <Label htmlFor={id}>{STAFF_IMPORT_PAGE_COPY.csvUploadLabel}</Label>
            <InputGroup
                $hasError={Boolean(resolvedError)}
                $clickable={!disabled && !uploading}
                onClick={openFilePicker}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openFilePicker();
                    }
                }}
            >
                <IconSlot $hasError={Boolean(resolvedError)} aria-hidden="true">
                    <Icon fasIcon="file-csv" />
                </IconSlot>
                <FileNameDisplay
                    $hasError={Boolean(resolvedError)}
                    $isPlaceholder={isPlaceholder}
                >
                    {displayName}
                </FileNameDisplay>
                <UploadButton
                    id={`${id}-upload`}
                    type="button"
                    variant="primary"
                    size="small"
                    $hasError={Boolean(resolvedError)}
                    $inProgress={uploading}
                    onClick={handleUploadClick}
                    disabled={!canUpload || uploadComplete}
                    loading={uploading}
                >
                    <Icon farIcon="upload" />
                    {STAFF_IMPORT_PAGE_COPY.uploadFileLabel}
                </UploadButton>
            </InputGroup>
            <HiddenFileInput
                ref={fileInputRef}
                id={id}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileInputChange}
                disabled={disabled || uploading}
            />
            {resolvedError ? (
                <ErrorMessage id={`${id}-error`} role="alert">
                    {resolvedError}
                </ErrorMessage>
            ) : null}
            <StaffImportWarningModal
                isOpen={showReplaceModal}
                modalName="staff-import-replace-file"
                title={STAFF_IMPORT_PAGE_COPY.replaceFileWarningTitle}
                message={STAFF_IMPORT_PAGE_COPY.replaceFileWarningMessage}
                dismissLabel={STAFF_IMPORT_PAGE_COPY.cancelLabel}
                confirmLabel={
                    STAFF_IMPORT_PAGE_COPY.replaceFileWarningUploadLabel
                }
                dismissButtonId="staff-import-replace-file-cancel"
                confirmButtonId="staff-import-replace-file-upload"
                onDismiss={handleReplaceCancel}
                onConfirm={handleReplaceConfirm}
            />
        </FieldContainer>
    );
};

export default StaffImportCsvUploadField;
